// apps/roleforge-app/src/components/canvas/OperatingModelPanel.tsx
//
// Plan 3e-i — Phase 3.1: Header + engines section + opportunities list view.
// Layout rework: SSE state is now sourced from `useOperatingModelState` so
// AssetsColumn and AssetEditorPane can share a single subscription. Callers
// may pass a pre-computed `state` prop to avoid double-subscribing; otherwise
// the panel subscribes internally for backwards compatibility.

import type { AuditRow } from '../../lib/api-client';
import type { SseEventRouter } from '../../lib/sse-event-router';
import {
  getEngineColor,
  visualTreatmentForCandidate,
} from '@vibecontrol/shared/roleforge';
import {
  useOperatingModelState,
  type OperatingModelOpportunity,
  type OperatingModelQuadrant,
  type OperatingModelState,
} from '../../lib/use-operating-model-state';
import { ValidationLockInAction } from '../audit/ValidationLockInAction';

export type ShellMode = 'building' | 'validating' | 'locked' | 'materializing' | 'materialized' | 'failed';

interface Props {
  audit: AuditRow;
  router: SseEventRouter;
  mode: ShellMode;
  refreshAudit: () => Promise<void>;
  jwt?: string | null;
  validationLocking?: boolean;
  validationLockError?: string | null;
  onValidationLockIn?: () => Promise<void>;
  /**
   * Optional pre-computed operating-model state. When provided, the panel
   * renders from this and skips its own subscription. Lets consumers (e.g.
   * AssetEditorPane) share a single subscription with AssetsColumn instead
   * of double-subscribing to the same router events.
   */
  state?: OperatingModelState;
}

export function OperatingModelPanel(props: Props) {
  if (props.state) {
    return (
      <OperatingModelPanelView
        audit={props.audit}
        mode={props.mode}
        state={props.state}
        jwt={props.jwt}
        refreshAudit={props.refreshAudit}
        validationLocking={props.validationLocking}
        validationLockError={props.validationLockError}
        onValidationLockIn={props.onValidationLockIn}
      />
    );
  }
  return (
    <OperatingModelPanelSubscribed
      audit={props.audit}
      router={props.router}
      mode={props.mode}
      jwt={props.jwt}
      refreshAudit={props.refreshAudit}
      validationLocking={props.validationLocking}
      validationLockError={props.validationLockError}
      onValidationLockIn={props.onValidationLockIn}
    />
  );
}

function OperatingModelPanelSubscribed({
  audit,
  router,
  mode,
  jwt,
  refreshAudit,
  validationLocking,
  validationLockError,
  onValidationLockIn,
}: {
  audit: AuditRow;
  router: SseEventRouter;
  mode: ShellMode;
  jwt?: string | null;
  refreshAudit: () => Promise<void>;
  validationLocking?: boolean;
  validationLockError?: string | null;
  onValidationLockIn?: () => Promise<void>;
}) {
  const state = useOperatingModelState(router);
  return (
    <OperatingModelPanelView
      audit={audit}
      mode={mode}
      state={state}
      jwt={jwt}
      refreshAudit={refreshAudit}
      validationLocking={validationLocking}
      validationLockError={validationLockError}
      onValidationLockIn={onValidationLockIn}
    />
  );
}

function OperatingModelPanelView({
  audit,
  mode,
  state,
  jwt,
  refreshAudit,
  validationLocking,
  validationLockError,
  onValidationLockIn,
}: {
  audit: AuditRow;
  mode: ShellMode;
  state: OperatingModelState;
  jwt?: string | null;
  refreshAudit: () => Promise<void>;
  validationLocking?: boolean;
  validationLockError?: string | null;
  onValidationLockIn?: () => Promise<void>;
}) {
  const { engines, opportunities, matrixAssembled, assignments, totalSavings } = state;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-ink">{audit.summary ?? 'AI Workforce Blueprint'}</h2>
        {audit.vertical && (
          <span className="text-xs uppercase tracking-wide text-graphite">{audit.vertical}</span>
        )}
      </header>

      <section aria-label="Engines" className="flex flex-col gap-3">
        {engines.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line p-6 text-center text-sm text-graphite">
            Your AI Workforce Blueprint will populate here as we talk.
          </div>
        ) : (
          engines.map((engine) => (
            <div
              key={engine.engine_id}
              className="rounded-lg border border-line bg-card shadow-sm"
              style={{ borderTopColor: getEngineColor(engine.engine_id), borderTopWidth: 4 }}
            >
              <div className="p-3">
                <h3 className="text-sm font-semibold text-ink">{engine.name}</h3>
                {engine.steps.length > 0 && (
                  <ol className="mt-2 space-y-1">
                    {engine.steps.map((s, idx) => (
                      <li key={s.step_id} className="flex items-start gap-2 text-xs text-graphite">
                        <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] text-graphite">
                          {idx + 1}
                        </span>
                        <span className="flex-1">{s.name}</span>
                        {s.time_sink && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                            Time Sink
                          </span>
                        )}
                        {s.quality_risk && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700">
                            Quality Risk
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          ))
        )}
      </section>

      <section
        aria-label="Opportunities"
        className="flex flex-col gap-2"
        data-testid={matrixAssembled ? 'opportunity-matrix-2x2' : 'opportunity-list'}
      >
        {matrixAssembled ? (
          <Matrix2x2 opportunities={opportunities} assignments={assignments} />
        ) : (
          opportunities.map((o) => <OpportunityCard key={o.opportunity_id} opportunity={o} />)
        )}
      </section>

      <footer className="mt-auto rounded-lg border border-line bg-surface p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-graphite">Estimated annual savings</span>
          <span className="font-semibold text-ink">${totalSavings.toLocaleString()}</span>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          {mode === 'locked' ? (
            <a
              href={`/audits/${audit.id}/materialize`}
              className="rounded bg-coral px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
              data-testid="cta-build-team"
            >
              Set it up in VibeControl
            </a>
          ) : (
            <button
              type="button"
              disabled
              aria-disabled
              title="Available at activation moment (next phase)"
              className="cursor-not-allowed rounded bg-surface px-3 py-1.5 text-sm font-medium text-paper"
              data-testid="cta-build-team"
            >
              Set it up in VibeControl
            </button>
          )}
          <span
            aria-disabled
            className="cursor-not-allowed text-xs text-graphite underline-offset-2"
            data-testid="cta-export-assets"
          >
            Download my Blueprint Bundle
          </span>
        </div>
        <p className="mt-1 text-[10px] text-graphite">Both actions activate at the end of the audit.</p>
      </footer>

      {mode === 'validating' && (
        <div className="rounded bg-brand-coral/5 p-2 text-xs text-coral">
          You're now reviewing my draft — drag, edit, or remove anything I got wrong.
        </div>
      )}

      {mode === 'validating' && (
        <ValidationLockInAction
          audit={audit}
          jwt={jwt}
          refreshAudit={refreshAudit}
          locking={validationLocking}
          lockError={validationLockError}
          onLockIn={onValidationLockIn}
        />
      )}
    </div>
  );
}

function Matrix2x2(props: { opportunities: OperatingModelOpportunity[]; assignments: Map<string, OperatingModelQuadrant> }) {
  const cells: Record<OperatingModelQuadrant, OperatingModelOpportunity[]> = {
    quick_wins: [],
    big_swings: [],
    nice_to_haves: [],
    deprioritize: [],
  };
  for (const o of props.opportunities) {
    const q = props.assignments.get(o.opportunity_id) ?? 'quick_wins';
    cells[q].push(o);
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <Quadrant label="Quick Wins" items={cells.quick_wins} />
      <Quadrant label="Nice-to-haves" items={cells.nice_to_haves} />
      <Quadrant label="Big Swings" items={cells.big_swings} />
      <Quadrant label="Deprioritize" items={cells.deprioritize} />
    </div>
  );
}

function Quadrant(props: { label: string; items: OperatingModelOpportunity[] }) {
  return (
    <div className="rounded border border-line bg-card p-2">
      <h5 className="mb-1 text-[10px] uppercase tracking-wide text-graphite">{props.label}</h5>
      <div className="flex flex-col gap-1">
        {props.items.map((o) => (
          <OpportunityCard key={o.opportunity_id} opportunity={o} />
        ))}
      </div>
    </div>
  );
}

function OpportunityCard({ opportunity }: { opportunity: OperatingModelOpportunity }) {
  const visual = visualTreatmentForCandidate(opportunity.candidate_class);
  const isGap = opportunity.candidate_class === 'gap';
  const isFoundation = opportunity.candidate_class === 'foundation';

  const borderClass = isGap
    ? 'border-coral border-l-4 border-solid'
    : visual.border === 'dashed'
      ? 'border-dashed border-line'
      : 'border-solid border-line';

  const chipClass = isFoundation
    ? 'rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-graphite'
    : 'rounded bg-brand-coral/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-coral';

  return (
    <div
      data-testid={`opp-tile-${opportunity.opportunity_id}`}
      data-source-message-id={opportunity.source_message_id ?? ''}
      className={`group rounded-lg border ${borderClass} bg-card p-3 shadow-sm cursor-help`}
      title={
        opportunity.source_message_id
          ? 'Hover to highlight the chat message that surfaced this'
          : undefined
      }
      onMouseEnter={() => {
        if (!opportunity.source_message_id) return;
        document.dispatchEvent(
          new CustomEvent('roleforge:highlight-message', {
            detail: { messageId: opportunity.source_message_id },
          }),
        );
      }}
      onMouseLeave={() => {
        document.dispatchEvent(
          new CustomEvent('roleforge:highlight-message', { detail: { messageId: null } }),
        );
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-ink">{opportunity.title}</h4>
        {visual.chip && <span className={chipClass}>{visual.chip}</span>}
      </div>
      {opportunity.roi && (
        <p className="mt-1 text-xs text-emerald-700">
          ${opportunity.roi.annual_savings.toLocaleString()}/yr saved
        </p>
      )}
    </div>
  );
}
